<script>
import DeveloperProfileResource from '../resources/developerprofile'
import ListingResource from '../resources/listing'
import ImageAvatar from '../components/ImageAvatar.vue'
import dateMixin from '../mixins/date'

export default {
    components: {
        ImageAvatar
    },
    mixins: [dateMixin],
    props: {
        profile: {
            type: DeveloperProfileResource,
            requred: true,
        },
        listing: {
            type: ListingResource,
            required: false,
        }
    },
    watch: {
        async profile() {
            this.loading = true
            await this.getProfileData()
            this.loading = false
        }
    },
    computed: {
        secondColumnWidth() {
            return this.$slots.default && $slots.default.length > 0 ? 4 : 0
        },
        headlineColWidthAndOffset() {
            let len = String(this.profile.attributes.headline).length

            if(len > 200) {
                return { width: 10, offset: 1 }
            } else if(len < 100) {
                return { width: 6, offset: 3 }
            } else {
                return { width: 8, offset: 2 }
            }
        }
    },
    data() {
        return {
            loading: false,
            experienceResources: [],
            educationResources: [],
            skillsResources: []
        }
    },
    methods: {
        async getProfileData() {
            await this.profile.resolveRelated()
            this.experienceResources = this.profile.getExperienceResourcesSorted()
            this.educationResources = this.profile.getEducationResourcesSorted()
            this.skillsResources = await this.profile.rel('skills').resolve()
        },
        stringMaxLength(input, maxLength) {
            if (input.length > maxLength) {
                return input.substring(0, maxLength) + '...'
            }

            return input
        },
        onClick(e) {
            this.$emit('click', e)
        }
    },
    async mounted() {
        this.loading = true
        await this.getProfileData()
        this.loading = false
    },
}
</script>

<template>
    <div>
        <div class="profile-header">
            <b-row class="header-details">
                <b-col>
                    <image-avatar :resource="profile" :rounded="false" variant="light" size="8rem" class="ml-2 mr-3 float-left" />
                    <div class="header-content">
                        <h4><a href="javascript:{}" @click="onClick">{{ profile }}</a></h4>
                        <h6>{{ profile.readonly.primary_role_name }}</h6>
                    </div>
                </b-col>
            </b-row>
        </div>
        
        <b-row v-if="!loading">
            <b-col>
                <b-row v-if="profile.attributes.headline" class="mt-3">
                    <b-col :md="headlineColWidthAndOffset.width" :offset="headlineColWidthAndOffset.offset">
                        <div class="text-muted headline">
                            <b-icon icon="chat-right-quote-fill" />
                            <p class="text-muted">{{ profile.attributes.headline }}</p>
                        </div>
                    </b-col>
                </b-row>

                <b-row class="profile">
                    <b-col>
                        <b-table-simple class="mt-3">
                            <b-tr>
                                <b-td>Education</b-td>
                                <b-td>
                                    <ul class="list-unstyled">
                                        <li v-for="(education, i) in educationResources" :key="i">
                                            {{ education }} <b-badge variant="info" pill v-if="education.isElite()"><b-icon icon="award-fill" /> Elite</b-badge>
                                        </li>
                                    </ul>
                                </b-td>
                            </b-tr>
                            <b-tr>
                                <b-td>Location</b-td>
                                <b-td>{{ profile.readonly.location_preferred_name }}</b-td>
                            </b-tr>
                            <b-tr>
                                <b-td>Looking For</b-td>
                                <b-td>{{ profile.readonly.looking_for_name }}</b-td>
                            </b-tr>
                            <b-tr>
                                <b-td>Remote Preference</b-td>
                                <b-td>{{ profile.readonly.remote_preference_name }}</b-td>
                            </b-tr>
                            <b-tr>
                                <b-td>Sponsorship</b-td>
                                <b-td>{{ profile.attributes.sponsorship_required ? 'Required' : 'Not Required' }}</b-td>
                            </b-tr>
                        </b-table-simple>
                    </b-col>

                    <b-col sm="4">
                        <ul class="list-unstyled mt-3">
                            <li v-if="profile.attributes.years_experience > 1" class="text-success"><b-icon icon="check" /> {{ profile.attributes.years_experience }} Years Experience</li>
                            <li class="text-success"><b-icon icon="check" /> Salary: {{ profile.salaryFormatted() }}</li>
                        </ul>
                    </b-col>
                </b-row>

                <div class="skills" v-if="skillsResources.length > 0">
                    <hr />
                    <b-badge pill v-for="(skillsResource, i) in skillsResources" :key="i" class="mr-1">{{ skillsResource }}</b-badge>
                </div>

                <hr />

                <b-card header="Experience">
                    <b-row v-for="(experience, i) in experienceResources" :key="i" :class="`experience ${i % 2 == 1 ? 'odd' : ''}`">
                        <b-col sm="3">
                            <h4 class="company-title text-right">{{ experience.attributes.company_name }}</h4>
                            <p class="text-right text-muted"><small>{{ dateMonthYearSpanPretty(experience.attributes.start_month, experience.attributes.start_year, experience.attributes.end_month, experience.attributes.end_year) }}</small></p>
                        </b-col>
                        <b-col>
                            <p class="lead">{{ experience.attributes.title }}</p>
                            <p>{{ experience.attributes.description }}</p>
                        </b-col>
                    </b-row>
                </b-card>
            </b-col>
        </b-row>

        <div v-else>
            <b-card class="mt-3">
                <b-skeleton animation="wave" width="85%"></b-skeleton>
                <b-skeleton animation="wave" width="55%"></b-skeleton>
                <b-skeleton animation="wave" width="70%"></b-skeleton>
            </b-card>

            <b-card class="mt-3">
                <b-skeleton animation="wave" width="85%"></b-skeleton>
                <b-skeleton animation="wave" width="55%"></b-skeleton>
            </b-card>
        </div>

    </div>
</template>

<style lang="scss" scoped>
.profile-header {
    padding-top: 140px;
    background: linear-gradient(135deg, #333f8c 0%, #3a96cc 100%);
    background-size: 100% 140px;
    background-repeat: no-repeat;

    .header-details {
        margin-top: -7rem;
    }
}

.headline {
    font-size: 1rem;
}

.b-avatar {
    cursor: pointer;
    border: 3px solid #fff;
}

.header-content {
    color: #fff;
    padding-top: 40px;

    a {
        color: #fff;
    }
}

.experience {
    font-size: 1rem;
    padding: 10px 0;
    margin-bottom: 10px;

    h4 { 
        font-size: 1.2em;
        margin-top: 3px;
    }

    &.odd {
        background: #f7f7f7;
    }
}
</style>
